<template>
  <div class="home-layout">
    <div v-if="auth.isAuthenticated" class="home-dashboard-mobile">
      <div class="dashboard-tab-bar" role="tablist" aria-label="Разделы поездок">
        <button
          v-for="tab in dashboardTabs"
          :key="tab.id"
          type="button"
          class="dashboard-tab"
          :class="{ 'dashboard-tab--active': activeTab === tab.id }"
          role="tab"
          :aria-selected="activeTab === tab.id"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </div>

      <section class="home-dashboard-panel panel">
        <div v-if="activeTab === 'personal'" class="home-dashboard-tab">
          <div class="column-header column-header--tabs">
            <div class="column-header-tabs-row">
              <div class="dashboard-tab-bar" role="tablist" aria-label="Мои и избранное">
                <button
                  v-for="tab in personalSubTabs"
                  :key="tab.id"
                  type="button"
                  class="dashboard-tab"
                  :class="{ 'dashboard-tab--active': personalSubTab === tab.id }"
                  role="tab"
                  :aria-selected="personalSubTab === tab.id"
                  @click="personalSubTab = tab.id"
                >
                  {{ tab.label }}
                </button>
              </div>
              <AddItemLink
                v-if="personalSubTab === 'my'"
                :to="{ name: 'trip-new' }"
                label="Добавить поездку"
              />
            </div>
          </div>
          <div v-if="personalSubTab === 'my'" class="column-body">
            <p v-if="myLoading" class="empty-state">Загрузка...</p>
            <TripList
              v-else
              :trips="myTrips"
              empty-text="Пока нет сохранённых поездок."
              @select="openTrip"
            />
          </div>
          <ListPager
            v-if="personalSubTab === 'my'"
            :page="myPage"
            :total-pages="myPagination?.total_pages || 0"
            :total="myPagination?.total || 0"
            :disabled="myLoading"
            @update:page="myPage = $event"
          />
          <div v-if="personalSubTab === 'favorites'" class="column-body">
            <p v-if="favoriteTripsLoading" class="empty-state">Загрузка...</p>
            <TripList
              v-else
              title="Поездки"
              :trips="favoriteTrips"
              empty-text="Пока нет избранных поездок."
              @select="openTrip"
            />
            <ListPager
              :page="favoriteTripsPage"
              :total-pages="favoriteTripsPagination?.total_pages || 0"
              :total="favoriteTripsPagination?.total || 0"
              :disabled="favoriteTripsLoading"
              @update:page="favoriteTripsPage = $event"
            />
            <AuthorFavoriteList
              title="Авторы"
              :authors="favoriteAuthors"
              :removing-id="removingAuthorId"
              @remove="removeFavoriteAuthor"
            />
            <ListPager
              :page="favoriteAuthorsPage"
              :total-pages="favoriteAuthorsPagination?.total_pages || 0"
              :total="favoriteAuthorsPagination?.total || 0"
              :disabled="favoriteAuthorsLoading"
              @update:page="favoriteAuthorsPage = $event"
            />
          </div>
        </div>

        <div v-else-if="activeTab === 'public'" class="home-dashboard-tab">
          <div class="column-header">
            <ColumnFilter
              v-model="publicTripsSort"
              :options="publicTripsFilterOptions"
              :loading="publicTripsLoading"
              @update:model-value="onPublicSortChange"
            />
          </div>
          <div class="column-body">
            <p v-if="publicTripsLoading" class="empty-state">Загрузка...</p>
            <TripList
              v-else
              :trips="publicTrips"
              empty-text="Пока нет публичных поездок."
              @select="openTrip"
            />
          </div>
          <ListPager
            :page="publicPage"
            :total-pages="publicPagination?.total_pages || 0"
            :total="publicPagination?.total || 0"
            :disabled="publicTripsLoading"
            @update:page="publicPage = $event"
          />
        </div>

        <div v-else class="home-dashboard-tab">
          <h2>Последние комментарии</h2>
          <div class="column-body">
            <RecentCommentFeed
              kind="trip"
              :comments="recentComments"
              :loading="recentCommentsLoading"
            />
            <LoadMoreButton
              :has-more="hasMorePages(recentCommentsPagination)"
              :loading="recentCommentsLoadingMore"
              :disabled="recentCommentsLoading"
              @click="loadMoreRecentComments"
            />
          </div>
        </div>
      </section>
    </div>

    <div v-if="auth.isAuthenticated" class="home-columns home-columns--desktop">
      <section class="dashboard-column panel">
        <div class="column-header column-header--tabs">
          <div class="column-header-tabs-row">
            <div class="dashboard-tab-bar" role="tablist" aria-label="Мои и избранное">
              <button
                v-for="tab in personalSubTabs"
                :key="tab.id"
                type="button"
                class="dashboard-tab"
                :class="{ 'dashboard-tab--active': personalSubTab === tab.id }"
                role="tab"
                :aria-selected="personalSubTab === tab.id"
                @click="personalSubTab = tab.id"
              >
                {{ tab.label }}
              </button>
            </div>
            <AddItemLink
              v-if="personalSubTab === 'my'"
              :to="{ name: 'trip-new' }"
              label="Добавить поездку"
            />
          </div>
        </div>
        <div v-if="personalSubTab === 'my'" class="column-body">
          <TripList
            :trips="myTrips"
            empty-text="Пока нет сохранённых поездок."
            @select="openTrip"
          />
        </div>
        <div v-else class="column-body">
          <TripList
            title="Поездки"
            :trips="favoriteTrips"
            empty-text="Пока нет избранных поездок."
            @select="openTrip"
          />
          <AuthorFavoriteList
            title="Авторы"
            :authors="favoriteAuthors"
            :removing-id="removingAuthorId"
            @remove="removeFavoriteAuthor"
          />
        </div>
      </section>

      <section class="dashboard-column panel">
        <div class="column-header">
          <ColumnFilter
            v-model="publicTripsSort"
            :options="publicTripsFilterOptions"
            :loading="publicTripsLoading"
            @update:model-value="loadPublicTripsDesktop"
          />
        </div>
        <div class="column-body">
          <p v-if="publicTripsLoading" class="empty-state">Загрузка...</p>
          <TripList
            v-else
            :trips="publicTrips"
            empty-text="Пока нет публичных поездок."
            @select="openTrip"
          />
        </div>
      </section>

      <section class="dashboard-column panel">
        <h2>Последние комментарии</h2>
        <div class="column-body">
          <RecentCommentFeed
            kind="trip"
            :comments="recentComments"
            :loading="recentCommentsLoading"
          />
          <LoadMoreButton
            :has-more="hasMorePages(recentCommentsPagination)"
            :loading="recentCommentsLoadingMore"
            :disabled="recentCommentsLoading"
            @click="loadMoreRecentComments"
          />
        </div>
      </section>
    </div>

    <div v-else class="guest-home">
      <div class="guest-home-mobile home-dashboard-mobile">
        <div class="dashboard-tab-bar" role="tablist" aria-label="Разделы поездок">
          <button
            v-for="tab in guestTabs"
            :key="tab.id"
            type="button"
            class="dashboard-tab"
            :class="{ 'dashboard-tab--active': guestActiveTab === tab.id }"
            role="tab"
            :aria-selected="guestActiveTab === tab.id"
            @click="guestActiveTab = tab.id"
          >
            {{ tab.label }}
          </button>
        </div>

        <section class="home-dashboard-panel panel">
          <div v-if="guestActiveTab === 'public'" class="home-dashboard-tab">
            <div class="column-header">
              <h1>Публичные поездки</h1>
              <ColumnFilter
                v-model="publicTripsSort"
                :options="publicTripsFilterOptions"
                :loading="publicTripsLoading"
                @update:model-value="loadPublicTripsDesktop"
              />
            </div>
            <div class="column-body">
              <p v-if="publicTripsLoading" class="empty-state">Загрузка...</p>
              <TripList
                v-else
                :trips="publicTrips"
                empty-text="Пока нет публичных поездок."
                @select="openTrip"
              />
            </div>
          </div>

          <div v-else class="home-dashboard-tab">
            <h2 class="guest-column-title">Последние комментарии</h2>
            <div class="column-body">
              <RecentCommentFeed
                kind="trip"
                :comments="recentComments"
                :loading="recentCommentsLoading"
              />
              <LoadMoreButton
                :has-more="hasMorePages(recentCommentsPagination)"
                :loading="recentCommentsLoadingMore"
                :disabled="recentCommentsLoading"
                @click="loadMoreRecentComments"
              />
            </div>
          </div>
        </section>
      </div>

      <div class="guest-home-columns guest-home-columns--desktop">
        <section class="panel guest-home-column">
          <div class="column-header">
            <h1>Публичные поездки</h1>
            <ColumnFilter
              v-model="publicTripsSort"
              :options="publicTripsFilterOptions"
              :loading="publicTripsLoading"
              @update:model-value="loadPublicTripsDesktop"
            />
          </div>
          <div class="column-body">
            <p v-if="publicTripsLoading" class="empty-state">Загрузка...</p>
            <TripList
              v-else
              :trips="publicTrips"
              empty-text="Пока нет публичных поездок."
              @select="openTrip"
            />
          </div>
        </section>

        <section class="panel guest-home-column">
          <h2 class="guest-column-title">Последние комментарии</h2>
          <div class="column-body">
            <RecentCommentFeed
              kind="trip"
              :comments="recentComments"
              :loading="recentCommentsLoading"
            />
            <LoadMoreButton
              :has-more="hasMorePages(recentCommentsPagination)"
              :loading="recentCommentsLoadingMore"
              :disabled="recentCommentsLoading"
              @click="loadMoreRecentComments"
            />
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import AddItemLink from '../components/AddItemLink.vue'
import AuthorFavoriteList from '../components/AuthorFavoriteList.vue'
import ColumnFilter from '../components/ColumnFilter.vue'
import ListPager from '../components/ListPager.vue'
import LoadMoreButton from '../components/LoadMoreButton.vue'
import RecentCommentFeed from '../components/RecentCommentFeed.vue'
import TripList from '../components/TripList.vue'
import { fetchFavoriteUsers, unfavoriteUser } from '../api/favorites'
import { fetchFavoriteTrips, fetchPublicTripComments, fetchPublicTrips, fetchTrips } from '../api/trips'
import { useMediaQuery } from '../composables/useMediaQuery'
import { useAuthStore } from '../stores/auth'
import { hasMorePages, parsePaginatedList } from '../utils/pagination'

const PAGE_SIZE = 10

const router = useRouter()
const auth = useAuthStore()
const isMobile = useMediaQuery('(max-width: 767px)')

const myTrips = ref([])
const publicTrips = ref([])
const favoriteTrips = ref([])
const favoriteAuthors = ref([])
const removingAuthorId = ref(null)
const publicTripsSort = ref('new')
const publicTripsLoading = ref(false)
const myLoading = ref(false)
const favoriteTripsLoading = ref(false)
const favoriteAuthorsLoading = ref(false)
const recentComments = ref([])
const recentCommentsLoading = ref(false)
const recentCommentsLoadingMore = ref(false)
const recentCommentsPage = ref(1)
const recentCommentsPagination = ref(null)

const activeTab = ref('personal')
const personalSubTab = ref('my')
const myPage = ref(1)
const publicPage = ref(1)
const favoriteTripsPage = ref(1)
const favoriteAuthorsPage = ref(1)
const myPagination = ref(null)
const publicPagination = ref(null)
const favoriteTripsPagination = ref(null)
const favoriteAuthorsPagination = ref(null)

const dashboardTabs = [
  { id: 'personal', label: 'Личное' },
  { id: 'public', label: 'На сайте' },
  { id: 'comments', label: 'Комментарии' },
]

const personalSubTabs = [
  { id: 'my', label: 'Мои поездки' },
  { id: 'favorites', label: 'Избранное' },
]

const guestActiveTab = ref('public')

const guestTabs = [
  { id: 'public', label: 'На сайте' },
  { id: 'comments', label: 'Комментарии' },
]

const publicTripsFilterOptions = [
  { value: 'new', label: 'Новые на сайте' },
  { value: 'most_commented', label: 'Самые обсуждаемые' },
  { value: 'most_liked', label: 'Самые популярные' },
]

async function loadMyTripsMobile() {
  myLoading.value = true
  try {
    const response = await fetchTrips({
      page: myPage.value,
      perPage: PAGE_SIZE,
    })
    const { items, pagination } = parsePaginatedList(response, 'trips')
    myTrips.value = items
    myPagination.value = pagination
  } finally {
    myLoading.value = false
  }
}

async function loadPublicTripsMobile() {
  publicTripsLoading.value = true
  try {
    const response = await fetchPublicTrips({
      sort: publicTripsSort.value,
      page: publicPage.value,
      perPage: PAGE_SIZE,
    })
    const { items, pagination } = parsePaginatedList(response, 'trips')
    publicTrips.value = items
    publicPagination.value = pagination
  } finally {
    publicTripsLoading.value = false
  }
}

async function loadFavoriteTripsMobile() {
  favoriteTripsLoading.value = true
  try {
    const response = await fetchFavoriteTrips({
      page: favoriteTripsPage.value,
      perPage: PAGE_SIZE,
    })
    const { items, pagination } = parsePaginatedList(response, 'trips')
    favoriteTrips.value = items
    favoriteTripsPagination.value = pagination
  } finally {
    favoriteTripsLoading.value = false
  }
}

async function loadFavoriteAuthorsMobile() {
  favoriteAuthorsLoading.value = true
  try {
    const response = await fetchFavoriteUsers({
      page: favoriteAuthorsPage.value,
      perPage: PAGE_SIZE,
    })
    const { items, pagination } = parsePaginatedList(response, 'authors')
    favoriteAuthors.value = items
    favoriteAuthorsPagination.value = pagination
  } finally {
    favoriteAuthorsLoading.value = false
  }
}

async function loadFavoritesMobile() {
  await Promise.all([loadFavoriteTripsMobile(), loadFavoriteAuthorsMobile()])
}

async function loadPublicTripsDesktop() {
  publicTripsLoading.value = true
  try {
    const response = await fetchPublicTrips({ sort: publicTripsSort.value })
    publicTrips.value = parsePaginatedList(response, 'trips').items
  } finally {
    publicTripsLoading.value = false
  }
}

async function loadDesktopTrips() {
  const [myResponse, favoriteTripsResponse, favoriteAuthorsResponse] = await Promise.all([
    fetchTrips(),
    fetchFavoriteTrips(),
    fetchFavoriteUsers(),
    loadRecentComments(),
  ])
  myTrips.value = parsePaginatedList(myResponse, 'trips').items
  favoriteTrips.value = parsePaginatedList(favoriteTripsResponse, 'trips').items
  favoriteAuthors.value = parsePaginatedList(favoriteAuthorsResponse, 'authors').items
  await loadPublicTripsDesktop()
}

async function loadPersonalMobile() {
  if (personalSubTab.value === 'my') {
    await loadMyTripsMobile()
  } else {
    await loadFavoritesMobile()
  }
}

async function loadMobileTab(tab = activeTab.value) {
  if (tab === 'personal') {
    await loadPersonalMobile()
  } else if (tab === 'public') {
    await loadPublicTripsMobile()
  } else {
    await loadRecentComments({ reset: true })
  }
}

async function loadRecentComments({ reset = true } = {}) {
  if (reset) {
    recentCommentsLoading.value = true
    recentCommentsPage.value = 1
  } else {
    recentCommentsLoadingMore.value = true
  }
  try {
    const response = await fetchPublicTripComments({
      page: recentCommentsPage.value,
      perPage: PAGE_SIZE,
    })
    const { items, pagination } = parsePaginatedList(response, 'comments')
    recentComments.value = reset ? items : [...recentComments.value, ...items]
    recentCommentsPagination.value = pagination
  } finally {
    recentCommentsLoading.value = false
    recentCommentsLoadingMore.value = false
  }
}

function loadMoreRecentComments() {
  recentCommentsPage.value += 1
  loadRecentComments({ reset: false })
}

async function loadTrips() {
  if (!auth.isAuthenticated) {
    await Promise.all([loadPublicTripsDesktop(), loadRecentComments({ reset: true })])
    return
  }

  if (isMobile.value) {
    await loadMobileTab()
  } else {
    await loadDesktopTrips()
  }
}

function onPublicSortChange() {
  publicPage.value = 1
  if (isMobile.value) {
    loadPublicTripsMobile()
  } else {
    loadPublicTripsDesktop()
  }
}

function openTrip(id) {
  router.push({ name: 'trip', params: { id } })
}

async function removeFavoriteAuthor(author) {
  removingAuthorId.value = author.id
  try {
    await unfavoriteUser(author.id)
    favoriteAuthors.value = favoriteAuthors.value.filter((item) => item.id !== author.id)
    if (isMobile.value) {
      await loadFavoriteAuthorsMobile()
    }
  } finally {
    removingAuthorId.value = null
  }
}

watch(isMobile, () => {
  loadTrips()
})

watch(activeTab, (tab) => {
  if (!auth.isAuthenticated || !isMobile.value) return
  loadMobileTab(tab)
})

watch(guestActiveTab, (tab) => {
  if (auth.isAuthenticated || !isMobile.value) return
  if (tab === 'comments') {
    loadRecentComments({ reset: true })
  }
})

watch(personalSubTab, () => {
  if (!auth.isAuthenticated || !isMobile.value || activeTab.value !== 'personal') return
  loadPersonalMobile()
})

watch(myPage, () => {
  if (!auth.isAuthenticated || !isMobile.value || activeTab.value !== 'personal' || personalSubTab.value !== 'my') return
  loadMyTripsMobile()
})

watch(publicPage, () => {
  if (!auth.isAuthenticated || !isMobile.value || activeTab.value !== 'public') return
  loadPublicTripsMobile()
})

watch(favoriteTripsPage, () => {
  if (!auth.isAuthenticated || !isMobile.value || activeTab.value !== 'personal' || personalSubTab.value !== 'favorites') return
  loadFavoriteTripsMobile()
})

watch(favoriteAuthorsPage, () => {
  if (!auth.isAuthenticated || !isMobile.value || activeTab.value !== 'personal' || personalSubTab.value !== 'favorites') return
  loadFavoriteAuthorsMobile()
})

onMounted(loadTrips)
</script>
